//* 장례식장 추가·수정 요청을 기존 문의(title/content)로 조립하는 순수 함수.
//* 이 형식이 곧 운영팀과의 약속이다 — 바꾸면 src/__adhoc__/enquiryQuickHelp.adhoc.test.tsx도 같이.
//* 슬랙 알림은 content 300자에서 잘리므로(enquiry.slack.ts) 식별 정보를 앞에, 메모를 맨 뒤에 둔다.
//* 관리자 문의 화면에는 전문이 보인다.

import {
  formatCoordMove,
  hasCoordValue,
} from '@shared/containers/FuneralHomeInfoEditor/funeralHomeAddressChange.util';
import { Coord } from '@shared/interfaces/map.interface';

export type FuneralHomeEnquiryMode = 'edit' | 'add';

export const FUNERAL_HOME_ENQUIRY_FIELDS = [
  'name',
  'address',
  //* 주소는 맞는데 지도 핀이 엉뚱한 곳에 찍히는 경우 — 검색으로 고른 자리의 좌표(+주소)를 담는다
  'mapLocation',
  'phoneNumber',
  'parkingAddress',
  'additionalInfo',
] as const;

export type FuneralHomeEnquiryField = typeof FUNERAL_HOME_ENQUIRY_FIELDS[number];

export const FUNERAL_HOME_ENQUIRY_FIELD_LABEL: Record<FuneralHomeEnquiryField, string> = {
  name: '이름',
  address: '주소',
  mapLocation: '지도 위치',
  phoneNumber: '전화번호',
  parkingAddress: '주차 안내',
  additionalInfo: '추가 안내',
};

export type FuneralHomeEnquiryFields = Partial<Record<FuneralHomeEnquiryField, string>>;

//* 지도 검색으로 고른 자리 → "주소 (x 경도, y 위도) · 위치 약 90m 이동".
//* x·y는 funeralHomeInfo.coord와 같은 축이라 그대로 옮겨 적으면 된다.
//* moveText(장례식장 수정 화면과 같은 formatCoordMove)는 핀을 얼마나 옮기는 요청인지 운영팀이 한눈에 보게 한다
export const formatMapLocation = (
  address: string,
  coord: { x: string; y: string },
  moveText?: string,
) => `${address.trim()} (x ${coord.x}, y ${coord.y})${moveText ? ` · ${moveText}` : ''}`;

//* 부분 좌표({x?,y?}·null)를 Coord로 — 빈 값·0은 hasCoordValue가 걸러 null
export const asCoord = (c?: { x?: string; y?: string } | null): Coord | null => {
  const coord = c?.x && c?.y ? { x: String(c.x), y: String(c.y) } : null;
  return hasCoordValue(coord) ? coord : null;
};

//* 장례식장 수정 화면과 같은 기준: 10m 안쪽은 같은 자리로 본다 (검색 출처마다 좌표 끝자리가 다르다)
export const SAME_MAP_LOCATION_TEXT = '지도 위치 동일';
export const isSameMapLocation = (
  from: { x?: string; y?: string } | null | undefined,
  to: { x: string; y: string },
) => {
  const before = asCoord(from);
  return !!before && formatCoordMove(before, to) === SAME_MAP_LOCATION_TEXT;
};

export type FuneralHomeEnquiryInput =
  | {
      mode: 'edit';
      target: { _id: string; name: string };
      //* 체크해서 바꾸기로 한 항목만. 빈 값은 줄을 뺀다
      changes: FuneralHomeEnquiryFields;
      memo?: string;
    }
  | {
      mode: 'add';
      fields: FuneralHomeEnquiryFields & { name: string; address: string };
      memo?: string;
    };

const clean = (value?: string) => (value ?? '').trim();

const fieldLines = (fields: FuneralHomeEnquiryFields, separator: string) =>
  FUNERAL_HOME_ENQUIRY_FIELDS.flatMap((key) => {
    const value = clean(fields[key]);
    return value ? [`${FUNERAL_HOME_ENQUIRY_FIELD_LABEL[key]}${separator}${value}`] : [];
  });

export const buildFuneralHomeEnquiry = (
  input: FuneralHomeEnquiryInput,
): { title: string; content: string } => {
  const memo = clean(input.memo);
  if (input.mode === 'edit') {
    const lines = [
      `장례식장: ${clean(input.target.name)}`,
      `ID: ${input.target._id}`,
      ...fieldLines(input.changes, ' → '),
    ];
    if (memo) lines.push(`메모 → ${memo}`);
    return {
      title: `[장례식장 수정] ${clean(input.target.name)}`,
      content: lines.join('\n'),
    };
  }
  const lines = fieldLines(input.fields, ': ');
  if (memo) lines.push(`메모: ${memo}`);
  return {
    title: `[장례식장 추가] ${clean(input.fields.name)}`,
    content: lines.join('\n'),
  };
};

//* 부고 작성 중 "장례식장 직접입력"(FuneralHomeInfoSearchForm)에서 넘어온 값 → 문의.
//* 부고는 embed로 바로 진행되고, 이 문의는 운영팀이 원본 장례식장을 고치는 데 쓴다.
//* - 고른 장례식장이 있으면(수정) 현재 값과 다른 항목만, 좌표가 바뀌었으면 지도 위치도
//* - 없으면(추가) 입력한 항목 전부 + 좌표
//* @returns 수정인데 바뀐 게 없으면 null (문의 안 보냄)
export interface FuneralHomeDirectInput {
  name: string;
  address?: string;
  phoneNumber?: string;
  parkingAddress?: string;
  coord?: { x?: string; y?: string };
}

export const buildFuneralHomeEnquiryFromDirectInput = (
  selected: (FuneralHomeDirectInput & { _id: string }) | null | undefined,
  input: FuneralHomeDirectInput,
): { title: string; content: string } | null => {
  const coord = asCoord(input.coord);
  const mapAddress = clean(input.address) || clean(input.name);

  if (!selected) {
    return buildFuneralHomeEnquiry({
      mode: 'add',
      fields: {
        name: clean(input.name),
        address: clean(input.address),
        mapLocation: coord ? formatMapLocation(mapAddress, coord) : '',
        phoneNumber: clean(input.phoneNumber),
        parkingAddress: clean(input.parkingAddress),
      },
    });
  }

  const changes: FuneralHomeEnquiryFields = {};
  const diff = (key: 'name' | 'address' | 'phoneNumber' | 'parkingAddress') => {
    const next = clean(input[key]);
    if (next !== clean(selected[key])) changes[key] = next;
  };
  diff('name');
  diff('address');
  diff('phoneNumber');
  diff('parkingAddress');
  const before = asCoord(selected.coord);
  //* 10m 안쪽 이동은 같은 자리 — 지도 위치 줄을 넣지 않는다
  if (coord && !isSameMapLocation(before, coord)) {
    changes.mapLocation = formatMapLocation(
      mapAddress,
      coord,
      formatCoordMove(before, coord),
    );
  }
  if (!hasFuneralHomeChanges(changes)) return null;
  return buildFuneralHomeEnquiry({
    mode: 'edit',
    target: { _id: selected._id, name: selected.name },
    changes,
    memo: '부고 작성 중 장례식장 직접입력에서 접수',
  });
};

//* 수정 요청에 실제로 담길 항목이 있는지 (체크만 하고 비워 두면 접수 안 됨)
export const hasFuneralHomeChanges = (changes: FuneralHomeEnquiryFields) =>
  FUNERAL_HOME_ENQUIRY_FIELDS.some((key) => clean(changes[key]).length > 0);
